/**
 * MCP Reminder API Mutations
 * React Query mutation hooks for creating and managing reminders
 *
 * Gateway mapping: /mcp-reminder/* → mcp-reminder service
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRoleMutationGate } from '@/api/_shared/roleGate';
import { canUseAdminTenantScope } from '@/utils/authz';
import { apiPatch, apiPost, apiPut } from '@/api/_shared/http';
import { roleReminderBase } from './paths';
import { reminderKeys } from './keys';
import type { CreateReminderRequest, Reminder } from './interfaces';

/**
 * Creates a new reminder for the current role scope (role-aware).
 * - ADMIN: POST /reminder/admin/create with clientId and companyId in body
 * - CLIENT: POST /reminder
 */
export const useCreateReminderMutation = () => {
  const queryClient = useQueryClient();
  const { role, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (body: CreateReminderRequest): Promise<Reminder> => {
      const endpoint = canUseAdminTenantScope(role)
        ? `${roleReminderBase(role)}/create`
        : roleReminderBase(role);
      const bodyWithScope = canUseAdminTenantScope(role)
        ? { ...body, clientId: selectedClientId || undefined, companyId: selectedCompanyId || undefined }
        : body;
      return apiPost<Reminder>(endpoint, bodyWithScope);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reminderKeys.all });
    }
  });
};

/**
 * Resolves (completes) a reminder by ID (role-aware).
 *
 * @param id - Reminder ID to resolve
 */
export const useResolveReminderMutation = () => {
  const queryClient = useQueryClient();
  const { role, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (id: string): Promise<Reminder> => {
      const endpoint = `${roleReminderBase(role)}/${id}/resolve`;
      const body = canUseAdminTenantScope(role)
        ? { clientId: selectedClientId || undefined, companyId: selectedCompanyId || undefined }
        : undefined;
      return apiPatch<Reminder>(endpoint, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reminderKeys.all });
    }
  });
};

/**
 * Updates reminder notification preferences for the current role scope (role-aware).
 *
 * @param defaultNotifyLeadMinutes - Default lead time in minutes before notifying
 */
export const useUpdateReminderPreferencesMutation = () => {
  const queryClient = useQueryClient();
  const { role, selectedClientId, selectedCompanyId } = useRoleMutationGate();

  return useMutation({
    mutationFn: async (defaultNotifyLeadMinutes: number) => {
      const body = canUseAdminTenantScope(role)
        ? {
            defaultNotifyLeadMinutes,
            clientId: selectedClientId || undefined,
            companyId: selectedCompanyId || undefined
          }
        : { defaultNotifyLeadMinutes };
      return apiPut(`${roleReminderBase(role)}/preferences`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...reminderKeys.all, 'preferences'] });
    }
  });
};
